import React from 'react';
import './LoopyAvatar.css';

export type LoopyState = 'neutral' | 'correct' | 'hint' | 'error' | 'thinking';
export type LoopySize = 'xs' | 'sm' | 'md';
export type LoopyEmotion =
  | 'default'
  // sheet 1
  | 'happy' | 'laugh' | 'wink' | 'think' | 'surprised'
  | 'sad' | 'good_job' | 'celebrate' | 'love' | 'cool'
  | 'study' | 'idea' | 'confused' | 'sleepy' | 'hello'
  | 'determined' | 'thankful' | 'shy' | 'error' | 'loading'
  // sheet 2
  | 'wave' | 'cheer' | 'clap' | 'yeah' | 'point' | 'ok'
  | 'shocked' | 'worried' | 'thinking' | 'hmm' | 'glasses' | 'eureka'
  | 'book' | 'pencil' | 'laptop' | 'magnifier' | 'heart' | 'bow'
  | 'tired' | 'sweat' | 'nervous' | 'angry' | 'pouting' | 'party';

interface LoopyAvatarProps {
  size?: LoopySize;
  state?: LoopyState;
  emotion?: LoopyEmotion;
  showBadge?: boolean;
  ariaLabel?: string;
  className?: string;
}

const emotionSrc = (emotion: LoopyEmotion): string =>
  emotion === 'default' ? '/avatars/loopy-avatar.png' : `/avatars/loopy/${emotion}.png`;

const BadgeIcon: React.FC<{ state: LoopyState }> = ({ state }) => {
  switch (state) {
    case 'correct':
      return (
        <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
          <path d="M2.5 6.2l2.4 2.4L9.5 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'error':
      return (
        <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
          <path d="M3 3l6 6M9 3l-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case 'thinking':
      return (
        <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
          <circle cx="3" cy="6" r="1" fill="currentColor" />
          <circle cx="6" cy="6" r="1" fill="currentColor" />
          <circle cx="9" cy="6" r="1" fill="currentColor" />
        </svg>
      );
    case 'hint':
    default:
      return (
        <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
          <path d="M6 1.5a3 3 0 00-1.8 5.4c.5.4.8.9.8 1.6V9h2v-.5c0-.7.3-1.2.8-1.6A3 3 0 006 1.5z" fill="currentColor" />
          <rect x="5" y="9.8" width="2" height="1.2" rx="0.4" fill="currentColor" />
        </svg>
      );
  }
};

const LoopyAvatar: React.FC<LoopyAvatarProps> = ({
  size = 'sm',
  state = 'neutral',
  emotion = 'default',
  showBadge = false,
  ariaLabel = 'Loopy',
  className = '',
}) => {
  const isEmotion = emotion !== 'default';
  return (
    <span
      className={`loopy-avatar loopy-avatar--${size} loopy-avatar--${state} ${isEmotion ? 'loopy-avatar--emotion' : ''} ${className}`.trim()}
      role="img"
      aria-label={ariaLabel}
    >
      <img
        src={emotionSrc(emotion)}
        alt=""
        className="loopy-avatar__img"
        draggable={false}
      />
      {showBadge && state !== 'neutral' && (
        <span className={`loopy-avatar__badge loopy-avatar__badge--${state}`} aria-hidden="true">
          <BadgeIcon state={state} />
        </span>
      )}
    </span>
  );
};

export default LoopyAvatar;
